import React, { useState, useEffect, useCallback } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, CartesianGrid, Legend } from "recharts";
import * as api from "../api";
import { MONTHS_FR } from "../constants";
import { C, R, F, CHART, numeric } from "../theme";
import { Icon, DataIcon } from "../icons";
import {
  Btn, IconBtn, Card, Chip, Input, Spinner, Textarea,
  EmptyState, Modal, Stat, SectionHeader, Callout,
} from "./UI";

// ─── Habillage commun des graphiques Recharts ─────────────────────────────────
const AXIS = { stroke: C.inkFaint, fontSize: 11, tickLine: false };
const TOOLTIP_STYLE = {
  background: C.surface,
  border: `1px solid ${C.border}`,
  borderRadius: R.md,
  fontSize: F.size.base,
  color: C.ink,
  boxShadow: "0 2px 6px rgba(10,37,64,.07)",
};

// Icône du mode de paiement (helper, pas un composant imbriqué)
function payIcon(method) {
  if (method === "cash") return "cash";
  if (method === "wave") return "waves";
  if (method === "card") return "card";
  return "mobile";
}
function payLabel(method) {
  if (method === "cash") return "Espèces";
  if (method === "wave") return "Wave";
  if (method === "card") return "Carte";
  return "Orange Money";
}

function Comptabilite({ user, etablissements, reloadEtabs }) {
  const [subTab, setSubTab] = useState("dashboard");
  const [etabId, setEtabId] = useState("");
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());

  // Dashboard data
  const [summary, setSummary] = useState(null);
  const [globalData, setGlobalData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [prevSummary, setPrevSummary] = useState(null);

  // Expenses
  const [expenses, setExpenses] = useState([]);
  const [expLoading, setExpLoading] = useState(false);
  const [showExpForm, setShowExpForm] = useState(false);
  const [editExp, setEditExp] = useState(null);
  const [expEtab, setExpEtab] = useState("");
  const [expCat, setExpCat] = useState("");
  const [expDesc, setExpDesc] = useState("");
  const [expAmount, setExpAmount] = useState("");
  const [expDate, setExpDate] = useState(new Date().toISOString().split("T")[0]);
  const [expPayMethod, setExpPayMethod] = useState("cash");
  const [expNotes, setExpNotes] = useState("");
  const [expSaving, setExpSaving] = useState(false);

  // Incomes
  const [incomes, setIncomes] = useState([]);
  const [incLoading, setIncLoading] = useState(false);
  const [showIncForm, setShowIncForm] = useState(false);
  const [editInc, setEditInc] = useState(null);
  const [incEtab, setIncEtab] = useState("");
  const [incDesc, setIncDesc] = useState("");
  const [incAmount, setIncAmount] = useState("");
  const [incDate, setIncDate] = useState(new Date().toISOString().split("T")[0]);
  const [incSource, setIncSource] = useState("autre");
  const [incNotes, setIncNotes] = useState("");
  const [incSaving, setIncSaving] = useState(false);

  // Categories
  const [categories, setCategories] = useState([]);

  const loadCategories = useCallback(async () => {
    try { setCategories(await api.adminGetExpenseCategories()); } catch {}
  }, []);

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const filters = { month, year };
      if (etabId) filters.etab_id = etabId;
      const s = await api.getFinanceSummary(filters);
      setSummary(s);
      if (user?.role === "admin") {
        const g = await api.getFinanceGlobal({ month, year });
        setGlobalData(g);
      }
      // Fetch previous month data for comparison
      const prevMonth = month === 1 ? 12 : month - 1;
      const prevYear = month === 1 ? year - 1 : year;
      const prevFilters = { month: prevMonth, year: prevYear };
      if (etabId) prevFilters.etab_id = etabId;
      api.getFinanceSummary(prevFilters).then(setPrevSummary).catch(() => setPrevSummary(null));
    } catch (e) { console.error(e); }
    finally { setSummaryLoading(false); }
  }, [month, year, etabId, user]);

  const loadExpenses = useCallback(async () => {
    setExpLoading(true);
    try {
      const filters = { month, year };
      if (etabId) filters.etab_id = etabId;
      setExpenses(await api.getExpenses(filters));
    } catch {} finally { setExpLoading(false); }
  }, [month, year, etabId]);

  const loadIncomes = useCallback(async () => {
    setIncLoading(true);
    try {
      const filters = { month, year };
      if (etabId) filters.etab_id = etabId;
      setIncomes(await api.getIncomes(filters));
    } catch {} finally { setIncLoading(false); }
  }, [month, year, etabId]);

  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => {
    if (subTab === "dashboard") loadSummary();
    if (subTab === "depenses") loadExpenses();
    if (subTab === "entrees") loadIncomes();
    if (subTab === "bilan") loadSummary();
  }, [subTab, loadSummary, loadExpenses, loadIncomes]);

  // ── Expense form handlers ──
  function openExpForm(e = null) {
    setEditExp(e);
    setExpEtab(e?.etab_id?.toString() || etabId || (etablissements[0]?.id?.toString() || ""));
    setExpCat(e?.category_id?.toString() || "");
    setExpDesc(e?.description || "");
    setExpAmount(e?.amount?.toString() || "");
    setExpDate(e?.expense_date ? new Date(e.expense_date).toISOString().split("T")[0] : new Date().toISOString().split("T")[0]);
    setExpPayMethod(e?.payment_method || "cash");
    setExpNotes(e?.notes || "");
    setShowExpForm(true);
  }
  async function submitExp(ev) {
    ev.preventDefault(); setExpSaving(true);
    try {
      const data = { etab_id: parseInt(expEtab), category_id: expCat ? parseInt(expCat) : null, description: expDesc, amount: parseFloat(expAmount), expense_date: expDate, payment_method: expPayMethod, notes: expNotes };
      if (editExp) await api.updateExpense(editExp.id, data);
      else await api.createExpense(data);
      setShowExpForm(false); loadExpenses(); loadSummary();
    } catch (err) { alert("Erreur : " + err.message); }
    finally { setExpSaving(false); }
  }
  async function deleteExp(e) {
    if (!window.confirm("Supprimer cette dépense ?")) return;
    try { await api.deleteExpense(e.id); loadExpenses(); loadSummary(); } catch (err) { alert("Erreur : " + err.message); }
  }

  // ── Income form handlers ──
  function openIncForm(inc = null) {
    setEditInc(inc);
    setIncEtab(inc?.etab_id?.toString() || etabId || (etablissements[0]?.id?.toString() || ""));
    setIncDesc(inc?.description || "");
    setIncAmount(inc?.amount?.toString() || "");
    setIncDate(inc?.income_date ? new Date(inc.income_date).toISOString().split("T")[0] : new Date().toISOString().split("T")[0]);
    setIncSource(inc?.source || "autre");
    setIncNotes(inc?.notes || "");
    setShowIncForm(true);
  }
  async function submitInc(ev) {
    ev.preventDefault(); setIncSaving(true);
    try {
      const data = { etab_id: parseInt(incEtab), description: incDesc, amount: parseFloat(incAmount), income_date: incDate, source: incSource, notes: incNotes };
      if (editInc) await api.updateIncome(editInc.id, data);
      else await api.createIncome(data);
      setShowIncForm(false); loadIncomes(); loadSummary();
    } catch (err) { alert("Erreur : " + err.message); }
    finally { setIncSaving(false); }
  }
  async function deleteInc(inc) {
    if (!window.confirm("Supprimer cette entrée ?")) return;
    try { await api.deleteIncome(inc.id); loadIncomes(); loadSummary(); } catch (err) { alert("Erreur : " + err.message); }
  }

  // ── CSV Export ──
  async function exportCSV() {
    try {
      const data = await api.getFinanceExport(month, year, etabId);
      let csv = "N\u00b0,Client,T\u00e9l\u00e9phone,Date,Statut,Montant,Paiement,M\u00e9thode\n";
      data.orders.forEach(o => {
        csv += `${o.id},"${o.client}",${o.phone},${o.date},${o.status},${o.total_amount || 0},${o.payment_status || ""},${o.payment_method || ""}\n`;
      });
      csv += "\n\nD\u00c9PENSES\nDescription,Cat\u00e9gorie,Montant,Date,M\u00e9thode\n";
      data.expenses.forEach(e => {
        csv += `"${e.description}","${e.category_name || ""}",${e.amount},${e.expense_date},${e.payment_method || ""}\n`;
      });
      const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Setal_Comptabilite_${year}-${String(month).padStart(2,"0")}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch(err) { alert(err.message); }
  }

  const fmt = (n) => (n || 0).toLocaleString("fr-FR") + " FCFA";
  // Variante sans unité : les composants <Stat> portent l'unité séparément
  const fmtN = (n) => (n || 0).toLocaleString("fr-FR");

  const SUB_TABS = [
    ["dashboard", "Tableau de bord", "dashboard"],
    ["depenses",  "Dépenses",        "trendDown"],
    ["entrees",   "Entrées",         "trendUp"],
    ["bilan",     "Bilan",           "document"],
  ];

  // ── Period selector (inline JSX — must NOT be a sub-component defined inside Comptabilite
  //    otherwise React unmounts/remounts on every render and the selects lose focus)
  const periodSelector = (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16, alignItems: "center" }}>
      <select className="field" style={{ width: "auto" }} aria-label="Établissement"
        value={etabId} onChange={e => setEtabId(e.target.value)}>
        <option value="">Tous les établissements</option>
        {(etablissements || []).filter(e => e.active).map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
      </select>
      <select className="field" style={{ width: "auto" }} aria-label="Mois"
        value={month} onChange={e => setMonth(parseInt(e.target.value))}>
        {MONTHS_FR.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
      </select>
      <input className="field" type="number" aria-label="Année"
        value={year} onChange={e => setYear(parseInt(e.target.value))}
        style={{ width: 92, ...numeric }} />
    </div>
  );

  return (
    <div>
      <div className="tabs" style={{ marginBottom: 16 }}>
        {SUB_TABS.map(([k, label, icon]) => (
          <button key={k} type="button" onClick={() => setSubTab(k)}
            className={`tab ${subTab === k ? "active" : ""}`}
            aria-current={subTab === k ? "page" : undefined}>
            <Icon name={icon} size={14} />
            {label}
          </button>
        ))}
      </div>

      {periodSelector}

      {/* ════ DASHBOARD ════ */}
      {subTab === "dashboard" && (
        <div>
          {summaryLoading ? <Spinner /> : summary ? (
            <div className="animate-fadeInUp">
              {/* Indicateurs */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 20 }}>
                {[
                  { label: "Revenus commandes", value: fmtN(summary.revenue.orders_total), unit: "FCFA", icon: "receipt",   tone: "navy",    cur: summary.revenue.orders_total, prev: prevSummary?.revenue?.orders_total },
                  { label: "Autres revenus",    value: fmtN(summary.revenue.other_income), unit: "FCFA", icon: "cash",      tone: "success", cur: summary.revenue.other_income, prev: prevSummary?.revenue?.other_income },
                  { label: "Total revenus",     value: fmtN(summary.revenue.total),        unit: "FCFA", icon: "trendUp",   tone: "success", cur: summary.revenue.total,        prev: prevSummary?.revenue?.total },
                  { label: "Total dépenses",    value: fmtN(summary.expenses.total),       unit: "FCFA", icon: "trendDown", tone: "danger",  cur: summary.expenses.total,       prev: prevSummary?.expenses?.total },
                  { label: "Bénéfice net",      value: fmtN(summary.profit),               unit: "FCFA",
                    icon: summary.profit >= 0 ? "trendUp" : "trendDown",
                    tone: summary.profit >= 0 ? "success" : "danger",
                    cur: summary.profit, prev: prevSummary?.profit },
                  { label: "Commandes",         value: fmtN(summary.orders_count),         unit: null,   icon: "package",   tone: "navy",    cur: summary.orders_count,         prev: prevSummary?.orders_count },
                ].map((kpi, i) => {
                  const pctChange = (kpi.prev != null && kpi.prev !== 0) ? ((kpi.cur - kpi.prev) / Math.abs(kpi.prev) * 100) : null;
                  return (
                    <Stat
                      key={i}
                      label={kpi.label}
                      value={kpi.value}
                      unit={kpi.unit}
                      icon={kpi.icon}
                      tone={kpi.tone}
                      trend={pctChange !== null ? Number(pctChange.toFixed(1)) : null}
                      hint={pctChange !== null ? "vs mois précédent" : undefined}
                    />
                  );
                })}
              </div>

              {/* Tendance mensuelle */}
              {summary.monthly_trend && summary.monthly_trend.length > 0 && (
                <Card style={{ marginBottom: 16 }}>
                  <SectionHeader title="Tendance mensuelle" subtitle="12 derniers mois" icon="chart" />
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={summary.monthly_trend.map(t => ({
                      ...t, month: new Date(t.month).toLocaleDateString("fr-FR", { month: "short", year: "2-digit" })
                    }))}>
                      <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="month" {...AXIS} />
                      <YAxis {...AXIS} tickFormatter={v => (v/1000) + "k"} />
                      <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: C.surfaceHover }}
                        formatter={(v) => v.toLocaleString("fr-FR") + " FCFA"} />
                      <Legend wrapperStyle={{ fontSize: F.size.sm, color: C.inkMuted }} />
                      <Bar dataKey="revenue" fill={CHART[0]} name="Revenus" radius={[R.sm, R.sm, 0, 0]} />
                      <Bar dataKey="expenses" fill={CHART[5]} name="Dépenses" radius={[R.sm, R.sm, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              )}

              {/* Revenus quotidiens */}
              {summary.daily_revenue && summary.daily_revenue.length > 0 && (
                <Card style={{ marginBottom: 16 }}>
                  <SectionHeader title="Revenus quotidiens" subtitle={`${MONTHS_FR[month-1]} ${year}`} icon="trendUp" />
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={summary.daily_revenue.map(d => ({
                      ...d, date: new Date(d.date).getDate()
                    }))}>
                      <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="date" {...AXIS} />
                      <YAxis {...AXIS} tickFormatter={v => (v/1000) + "k"} />
                      <Tooltip contentStyle={TOOLTIP_STYLE}
                        formatter={(v) => v.toLocaleString("fr-FR") + " FCFA"} />
                      <Line type="monotone" dataKey="revenue" stroke={CHART[0]} strokeWidth={2}
                        dot={{ r: 2.5, fill: CHART[0], strokeWidth: 0 }} activeDot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </Card>
              )}

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 16, marginBottom: 16 }}>
                {/* Dépenses par catégorie */}
                {summary.expenses.by_category && summary.expenses.by_category.length > 0 && (
                  <Card padded={false}>
                    <div style={{ padding: "16px 16px 0" }}>
                      <SectionHeader title="Dépenses par catégorie" icon="chart" />
                    </div>
                    <div style={{ padding: "0 16px" }}>
                      <ResponsiveContainer width="100%" height={230}>
                        <PieChart>
                          <Pie data={summary.expenses.by_category} dataKey="amount" nameKey="name"
                            cx="50%" cy="50%" outerRadius={78} innerRadius={44} paddingAngle={1}
                            stroke={C.surface} strokeWidth={1}
                            label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`}
                            labelLine={{ stroke: C.border }}>
                            {summary.expenses.by_category.map((_, i) => <Cell key={i} fill={CHART[i % CHART.length]} />)}
                          </Pie>
                          <Tooltip contentStyle={TOOLTIP_STYLE}
                            formatter={(v) => v.toLocaleString("fr-FR") + " FCFA"} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="tbl-wrap" style={{ border: "none", borderTop: `1px solid ${C.border}`, borderRadius: 0 }}>
                      <table className="tbl">
                        <thead>
                          <tr><th>Catégorie</th><th className="num">Montant</th></tr>
                        </thead>
                        <tbody>
                          {summary.expenses.by_category.map((c, i) => (
                            <tr key={i}>
                              <td>
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: CHART[i % CHART.length], flexShrink: 0 }} />
                                  <DataIcon value={c.icon} size={14} fallback="tag" />
                                  <span style={{ color: C.ink }}>{c.name}</span>
                                </span>
                              </td>
                              <td className="num" style={numeric}>{fmt(c.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                )}

                {/* Modes de paiement */}
                {summary.payment_methods && summary.payment_methods.length > 0 && (
                  <Card padded={false}>
                    <div style={{ padding: "16px 16px 0" }}>
                      <SectionHeader title="Modes de paiement" icon="card" />
                    </div>
                    <div className="tbl-wrap" style={{ border: "none" }}>
                      <table className="tbl">
                        <thead>
                          <tr><th>Méthode</th><th className="num">Commandes</th><th className="num">Total</th></tr>
                        </thead>
                        <tbody>
                          {summary.payment_methods.map((pm, i) => (
                            <tr key={i}>
                              <td>
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 8, color: C.ink, fontWeight: F.weight.medium }}>
                                  <Icon name={payIcon(pm.method)} size={15} color={C.inkMuted} />
                                  {payLabel(pm.method)}
                                </span>
                              </td>
                              <td className="num" style={numeric}>{pm.count}</td>
                              <td className="num" style={numeric}>{fmt(pm.total)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                )}
              </div>

              {/* Comparaison des établissements (admin) */}
              {globalData && globalData.etablissements && globalData.etablissements.length > 1 && (
                <Card style={{ marginBottom: 16 }}>
                  <SectionHeader title="Comparaison des établissements" subtitle={`${MONTHS_FR[month-1]} ${year}`} icon="building" />
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={globalData.etablissements}>
                      <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" {...AXIS} />
                      <YAxis {...AXIS} tickFormatter={v => (v/1000) + "k"} />
                      <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: C.surfaceHover }}
                        formatter={(v) => v.toLocaleString("fr-FR") + " FCFA"} />
                      <Legend wrapperStyle={{ fontSize: F.size.sm, color: C.inkMuted }} />
                      <Bar dataKey="revenue" fill={CHART[0]} name="Revenus" radius={[R.sm, R.sm, 0, 0]} />
                      <Bar dataKey="expenses" fill={CHART[5]} name="Dépenses" radius={[R.sm, R.sm, 0, 0]} />
                      <Bar dataKey="profit" fill={CHART[3]} name="Bénéfice" radius={[R.sm, R.sm, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="tbl-wrap" style={{ marginTop: 16 }}>
                    <table className="tbl">
                      <thead>
                        <tr>
                          <th>Établissement</th>
                          <th className="num">Revenus</th>
                          <th className="num">Dépenses</th>
                          <th className="num">Bénéfice</th>
                          <th className="num">Commandes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {globalData.etablissements.map((e, i) => (
                          <tr key={i}>
                            <td style={{ fontWeight: F.weight.medium, color: C.ink }}>{e.name}</td>
                            <td className="num" style={{ color: C.success, ...numeric }}>{fmt(e.revenue)}</td>
                            <td className="num" style={{ color: C.danger, ...numeric }}>{fmt(e.expenses)}</td>
                            <td className="num" style={{ color: e.profit >= 0 ? C.success : C.danger, fontWeight: F.weight.semi, ...numeric }}>{fmt(e.profit)}</td>
                            <td className="num" style={numeric}>{e.orders}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ background: C.surfaceAlt }}>
                          <td style={{ padding: "10px 12px", fontWeight: F.weight.bold, color: C.ink, borderTop: `1px solid ${C.border}` }}>Total</td>
                          <td className="num" style={{ padding: "10px 12px", color: C.success, fontWeight: F.weight.semi, borderTop: `1px solid ${C.border}`, ...numeric }}>{fmt(globalData.global.revenue)}</td>
                          <td className="num" style={{ padding: "10px 12px", color: C.danger, fontWeight: F.weight.semi, borderTop: `1px solid ${C.border}`, ...numeric }}>{fmt(globalData.global.expenses)}</td>
                          <td className="num" style={{ padding: "10px 12px", color: globalData.global.profit >= 0 ? C.success : C.danger, fontWeight: F.weight.bold, borderTop: `1px solid ${C.border}`, ...numeric }}>{fmt(globalData.global.profit)}</td>
                          <td className="num" style={{ padding: "10px 12px", fontWeight: F.weight.semi, borderTop: `1px solid ${C.border}`, ...numeric }}>{globalData.global.orders}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </Card>
              )}
            </div>
          ) : (
            <Card padded={false}>
              <EmptyState icon="chart" title="Aucune donnée disponible"
                description="Aucun mouvement enregistré pour cette période." />
            </Card>
          )}
        </div>
      )}

      {/* ════ DEPENSES ════ */}
      {subTab === "depenses" && (
        <div className="animate-fadeInUp">
          <SectionHeader
            title="Dépenses"
            subtitle={`${MONTHS_FR[month-1]} ${year}`}
            icon="trendDown"
            actions={<Btn small icon="add" onClick={() => openExpForm()}>Nouvelle dépense</Btn>}
          />
          {expLoading ? <Spinner /> : expenses.length === 0 ? (
            <Card padded={false}>
              <EmptyState
                icon="receipt"
                title="Aucune dépense pour cette période"
                description="Enregistrez vos charges pour suivre la rentabilité du mois."
                action={<Btn icon="add" onClick={() => openExpForm()}>Ajouter une dépense</Btn>}
              />
            </Card>
          ) : (
            <>
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
                background: C.dangerSoft, border: `1px solid ${C.dangerBorder}`, borderRadius: R.lg,
                padding: "11px 15px", marginBottom: 12,
              }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontWeight: F.weight.medium, color: C.danger, fontSize: F.size.md }}>
                  <Icon name="trendDown" size={15} />
                  Total dépenses du mois
                </span>
                <span style={{ fontWeight: F.weight.bold, color: C.danger, fontSize: F.size.xl, ...numeric }}>
                  {fmt(expenses.reduce((s, e) => s + parseFloat(e.amount), 0))}
                </span>
              </div>

              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Catégorie</th>
                      <th>Établissement</th>
                      <th>Date</th>
                      <th>Mode</th>
                      <th className="num">Montant</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {expenses.map(e => (
                      <tr key={e.id}>
                        <td style={{ maxWidth: 260 }}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                            <DataIcon value={e.category_icon} size={15} fallback="package" />
                            <span style={{ fontWeight: F.weight.medium, color: C.ink }}>{e.description}</span>
                          </span>
                          {e.notes && (
                            <div className="truncate" style={{ fontSize: F.size.sm, color: C.inkFaint, maxWidth: 250, marginTop: 2 }}>{e.notes}</div>
                          )}
                        </td>
                        <td>{e.category_name ? <Chip tone="navy" small>{e.category_name}</Chip> : <span style={{ color: C.inkFaint }}>—</span>}</td>
                        <td>{e.etab_name ? <Chip tone="warning" icon="building" small>{e.etab_name}</Chip> : <span style={{ color: C.inkFaint }}>—</span>}</td>
                        <td style={{ color: C.inkMuted, whiteSpace: "nowrap", ...numeric }}>
                          {new Date(e.expense_date).toLocaleDateString("fr-FR")}
                        </td>
                        <td>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: C.inkMuted }}>
                            <Icon name={payIcon(e.payment_method)} size={14} />
                            {payLabel(e.payment_method)}
                          </span>
                        </td>
                        <td className="num" style={{ color: C.danger, fontWeight: F.weight.semi, whiteSpace: "nowrap", ...numeric }}>
                          {fmt(parseFloat(e.amount))}
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: 4 }}>
                            <IconBtn icon="edit" title="Modifier la dépense" variant="outline" onClick={() => openExpForm(e)} />
                            <IconBtn icon="delete" title="Supprimer la dépense" variant="outline" danger onClick={() => deleteExp(e)} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {showExpForm && (
            <Modal
              title={editExp ? "Modifier la dépense" : "Nouvelle dépense"}
              icon={editExp ? "edit" : "trendDown"}
              width={520}
              onClose={() => setShowExpForm(false)}
            >
              <form onSubmit={submitExp}>
                <div style={{ marginBottom: 14 }}>
                  <label htmlFor="exp-etab" style={{ display: "flex", alignItems: "center", gap: 5, fontWeight: F.weight.medium, marginBottom: 5, fontSize: F.size.base, color: C.inkBody }}>
                    Établissement <span style={{ color: C.danger }} aria-hidden="true">*</span>
                  </label>
                  <select id="exp-etab" className="field" value={expEtab} onChange={e => setExpEtab(e.target.value)} required>
                    <option value="">Choisir…</option>
                    {(etablissements || []).filter(e => e.active).map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </select>
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label htmlFor="exp-cat" style={{ display: "block", fontWeight: F.weight.medium, marginBottom: 5, fontSize: F.size.base, color: C.inkBody }}>
                    Catégorie
                  </label>
                  <select id="exp-cat" className="field" value={expCat} onChange={e => setExpCat(e.target.value)}>
                    <option value="">Sans catégorie</option>
                    {(categories || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <Input label="Description" value={expDesc} onChange={setExpDesc} placeholder="ex : achat produits nettoyage" required />
                <Input label="Montant" hint="FCFA" icon="money" value={expAmount} onChange={setExpAmount} type="number" placeholder="0" required />
                <Input label="Date" value={expDate} onChange={setExpDate} type="date" required />
                <div style={{ marginBottom: 14 }}>
                  <span style={{ display: "block", fontWeight: F.weight.medium, marginBottom: 5, fontSize: F.size.base, color: C.inkBody }}>
                    Mode de paiement
                  </span>
                  <div style={{ display: "flex", gap: 8 }}>
                    {[["cash","Espèces"],["wave","Wave"],["orange_money","Orange Money"]].map(([v,l]) => (
                      <label key={v} style={{
                        flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
                        position: "relative", padding: "8px 6px", borderRadius: R.md, cursor: "pointer",
                        border: `1px solid ${expPayMethod === v ? C.navy : C.border}`,
                        background: expPayMethod === v ? C.navySoft : C.surface,
                        color: expPayMethod === v ? C.navy : C.inkMuted,
                        fontSize: F.size.base, fontWeight: F.weight.medium,
                      }}>
                        <input type="radio" name="expPay" value={v} checked={expPayMethod === v}
                          onChange={() => setExpPayMethod(v)} style={{ position: "absolute", opacity: 0, width: 0, height: 0 }} />
                        <Icon name={payIcon(v === "orange_money" ? "om" : v)} size={14} />
                        {l}
                      </label>
                    ))}
                  </div>
                </div>
                <Textarea label="Notes" value={expNotes} onChange={setExpNotes} />
                <div style={{ display: "flex", gap: 10 }}>
                  <Btn type="submit" icon="save" style={{ flex: 1 }} loading={expSaving} disabled={expSaving}>Enregistrer</Btn>
                  <Btn variant="ghost" onClick={() => setShowExpForm(false)}>Annuler</Btn>
                </div>
              </form>
            </Modal>
          )}
        </div>
      )}

      {/* ════ ENTREES ════ */}
      {subTab === "entrees" && (
        <div className="animate-fadeInUp">
          <SectionHeader
            title="Entrées"
            subtitle={`Revenus supplémentaires — ${MONTHS_FR[month-1]} ${year}`}
            icon="trendUp"
            actions={<Btn small icon="add" onClick={() => openIncForm()}>Nouvelle entrée</Btn>}
          />
          <Callout tone="info" icon="idea" style={{ marginBottom: 12 }}>
            Les revenus des commandes sont calculés automatiquement. Ajoutez ici les revenus
            supplémentaires (pourboires, locations, etc.).
          </Callout>

          {incLoading ? <Spinner /> : incomes.length === 0 ? (
            <Card padded={false}>
              <EmptyState
                icon="cash"
                title="Aucune entrée supplémentaire"
                description="Rien d'enregistré pour cette période."
                action={<Btn icon="add" onClick={() => openIncForm()}>Ajouter une entrée</Btn>}
              />
            </Card>
          ) : (
            <>
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
                background: C.successSoft, border: `1px solid ${C.successBorder}`, borderRadius: R.lg,
                padding: "11px 15px", marginBottom: 12,
              }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontWeight: F.weight.medium, color: C.success, fontSize: F.size.md }}>
                  <Icon name="trendUp" size={15} />
                  Total entrées supplémentaires
                </span>
                <span style={{ fontWeight: F.weight.bold, color: C.success, fontSize: F.size.xl, ...numeric }}>
                  {fmt(incomes.reduce((s, i) => s + parseFloat(i.amount), 0))}
                </span>
              </div>

              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Établissement</th>
                      <th>Source</th>
                      <th>Date</th>
                      <th className="num">Montant</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {incomes.map(inc => (
                      <tr key={inc.id}>
                        <td style={{ maxWidth: 280 }}>
                          <span style={{ fontWeight: F.weight.medium, color: C.ink }}>{inc.description}</span>
                          {inc.notes && (
                            <div className="truncate" style={{ fontSize: F.size.sm, color: C.inkFaint, maxWidth: 270, marginTop: 2 }}>{inc.notes}</div>
                          )}
                        </td>
                        <td>{inc.etab_name ? <Chip tone="warning" icon="building" small>{inc.etab_name}</Chip> : <span style={{ color: C.inkFaint }}>—</span>}</td>
                        <td><Chip tone="success" small>{inc.source}</Chip></td>
                        <td style={{ color: C.inkMuted, whiteSpace: "nowrap", ...numeric }}>
                          {new Date(inc.income_date).toLocaleDateString("fr-FR")}
                        </td>
                        <td className="num" style={{ color: C.success, fontWeight: F.weight.semi, whiteSpace: "nowrap", ...numeric }}>
                          +{fmt(parseFloat(inc.amount))}
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: 4 }}>
                            <IconBtn icon="edit" title="Modifier l'entrée" variant="outline" onClick={() => openIncForm(inc)} />
                            <IconBtn icon="delete" title="Supprimer l'entrée" variant="outline" danger onClick={() => deleteInc(inc)} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {showIncForm && (
            <Modal
              title={editInc ? "Modifier l'entrée" : "Nouvelle entrée"}
              icon={editInc ? "edit" : "trendUp"}
              width={520}
              onClose={() => setShowIncForm(false)}
            >
              <form onSubmit={submitInc}>
                <div style={{ marginBottom: 14 }}>
                  <label htmlFor="inc-etab" style={{ display: "flex", alignItems: "center", gap: 5, fontWeight: F.weight.medium, marginBottom: 5, fontSize: F.size.base, color: C.inkBody }}>
                    Établissement <span style={{ color: C.danger }} aria-hidden="true">*</span>
                  </label>
                  <select id="inc-etab" className="field" value={incEtab} onChange={e => setIncEtab(e.target.value)} required>
                    <option value="">Choisir…</option>
                    {(etablissements || []).filter(e => e.active).map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </select>
                </div>
                <Input label="Description" value={incDesc} onChange={setIncDesc} placeholder="ex : pourboire client" required />
                <Input label="Montant" hint="FCFA" icon="money" value={incAmount} onChange={setIncAmount} type="number" placeholder="0" required />
                <Input label="Date" value={incDate} onChange={setIncDate} type="date" required />
                <div style={{ marginBottom: 14 }}>
                  <label htmlFor="inc-source" style={{ display: "block", fontWeight: F.weight.medium, marginBottom: 5, fontSize: F.size.base, color: C.inkBody }}>
                    Source
                  </label>
                  <select id="inc-source" className="field" value={incSource} onChange={e => setIncSource(e.target.value)}>
                    <option value="autre">Autre</option>
                    <option value="pourboire">Pourboire</option>
                    <option value="location">Location équipement</option>
                    <option value="sous-traitance">Sous-traitance</option>
                    <option value="partenariat">Partenariat</option>
                  </select>
                </div>
                <Textarea label="Notes" value={incNotes} onChange={setIncNotes} />
                <div style={{ display: "flex", gap: 10 }}>
                  <Btn type="submit" icon="save" style={{ flex: 1 }} loading={incSaving} disabled={incSaving}>Enregistrer</Btn>
                  <Btn variant="ghost" onClick={() => setShowIncForm(false)}>Annuler</Btn>
                </div>
              </form>
            </Modal>
          )}
        </div>
      )}

      {/* ════ BILAN ════ */}
      {subTab === "bilan" && (
        <div className="animate-fadeInUp">
          <SectionHeader title="Bilan financier" subtitle={`${MONTHS_FR[month-1]} ${year}`} icon="document" />
          {summaryLoading ? <Spinner /> : summary ? (
            <div id="bilan-content">
              <Card padded={false} style={{ marginBottom: 16 }}>
                <div style={{ padding: "16px 16px 0" }}>
                  <SectionHeader title="Revenus" icon="trendUp" />
                </div>
                <div className="tbl-wrap" style={{ border: "none" }}>
                  <table className="tbl">
                    <tbody>
                      <tr>
                        <td style={{ color: C.inkBody }}>
                          Revenus des commandes
                          <span style={{ color: C.inkMuted, ...numeric }}> ({summary.orders_count} commandes)</span>
                        </td>
                        <td className="num" style={numeric}>{fmt(summary.revenue.orders_total)}</td>
                      </tr>
                      <tr>
                        <td style={{ color: C.inkBody }}>Autres revenus</td>
                        <td className="num" style={numeric}>{fmt(summary.revenue.other_income)}</td>
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr style={{ background: C.surfaceAlt }}>
                        <td style={{ padding: "10px 12px", fontWeight: F.weight.semi, color: C.ink, borderTop: `1px solid ${C.border}` }}>Total revenus</td>
                        <td className="num" style={{ padding: "10px 12px", fontWeight: F.weight.bold, color: C.success, borderTop: `1px solid ${C.border}`, ...numeric }}>
                          {fmt(summary.revenue.total)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </Card>

              <Card padded={false} style={{ marginBottom: 16 }}>
                <div style={{ padding: "16px 16px 0" }}>
                  <SectionHeader title="Dépenses" icon="trendDown" />
                </div>
                {summary.expenses.by_category.length === 0 ? (
                  <EmptyState icon="receipt" title="Aucune dépense enregistrée" compact />
                ) : (
                  <div className="tbl-wrap" style={{ border: "none" }}>
                    <table className="tbl">
                      <tbody>
                        {summary.expenses.by_category.map((c, i) => (
                          <tr key={i}>
                            <td>
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 8, color: C.inkBody }}>
                                <DataIcon value={c.icon} size={15} fallback="tag" />
                                {c.name}
                              </span>
                            </td>
                            <td className="num" style={numeric}>{fmt(c.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ background: C.surfaceAlt }}>
                          <td style={{ padding: "10px 12px", fontWeight: F.weight.semi, color: C.ink, borderTop: `1px solid ${C.border}` }}>Total dépenses</td>
                          <td className="num" style={{ padding: "10px 12px", fontWeight: F.weight.bold, color: C.danger, borderTop: `1px solid ${C.border}`, ...numeric }}>
                            {fmt(summary.expenses.total)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </Card>

              <Card style={{
                background: summary.profit >= 0 ? C.successSoft : C.dangerSoft,
                border: `1px solid ${summary.profit >= 0 ? C.successBorder : C.dangerBorder}`,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                  <div>
                    <div style={{
                      display: "inline-flex", alignItems: "center", gap: 7,
                      fontSize: F.size.md, fontWeight: F.weight.semi,
                      color: summary.profit >= 0 ? C.success : C.danger,
                    }}>
                      <Icon name={summary.profit >= 0 ? "trendUp" : "trendDown"} size={16} />
                      {summary.profit >= 0 ? "Bénéfice net" : "Perte nette"}
                    </div>
                    <div style={{ fontSize: F.size.sm, color: C.inkMuted, marginTop: 2 }}>Revenus − Dépenses</div>
                  </div>
                  <div style={{
                    fontSize: F.size["3xl"], fontWeight: F.weight.bold, letterSpacing: "-.02em",
                    color: summary.profit >= 0 ? C.success : C.danger, ...numeric,
                  }}>
                    {fmt(Math.abs(summary.profit))}
                  </div>
                </div>
                {summary.revenue.total > 0 && (
                  <div style={{ marginTop: 12, fontSize: F.size.base, color: C.inkMuted, ...numeric }}>
                    Marge : {((summary.profit / summary.revenue.total) * 100).toFixed(1)} %
                  </div>
                )}
              </Card>

              <div className="no-print" style={{ marginTop: 16, display: "flex", gap: 10, flexWrap: "wrap" }}>
                <Btn icon="print" onClick={() => window.print()}>Imprimer le bilan</Btn>
                <Btn variant="white" icon="download" onClick={exportCSV}>Exporter CSV</Btn>
              </div>
            </div>
          ) : (
            <Card padded={false}>
              <EmptyState icon="document" title="Aucune donnée"
                description="Aucun mouvement enregistré pour cette période." />
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

export default Comptabilite;
